import { Button } from "@9amhealth/shared";
import type { FC } from "react";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Path from "src/constants/path";
import { toast, useBloc } from "src/state/state";

import { createAppPath } from "src/lib/createAppPath";
import { extractEmailFromVerificationToken } from "src/lib/extractEmailFromVerificationToken";
import UserEmailVerificationCubit from "src/state/UserEmailVerificationCubit/UserEmailVerificationCubit";
import { UserEmailVerificationStateName } from "src/state/UserEmailVerificationCubit/UserEmailVerificationState";
import Loader from "src/ui/components/Loader/Loader";
import ErrorPage, {
  CenterEl,
  ErrorPageLayout
} from "src/ui/components/SentryBoundary/ErrorPage";
import Translate from "src/ui/components/Translate/Translate";

const useQuery = (): URLSearchParams => {
  return new URLSearchParams(useLocation().search);
};

const ConfirmEmailAddress: FC = () => {
  const navigate = useNavigate();
  const [showResend, setShowResend] = React.useState(true);

  const [
    userVerify,
    { verifyEmailByLink, initiateVerification, resendFailed }
  ] = useBloc(UserEmailVerificationCubit, {
    create: () => new UserEmailVerificationCubit()
  });

  const queryParams = useQuery();

  const linkVerificationToken = queryParams.get("code");

  const handleResend = async () => {
    const token = queryParams.get("code") ?? "";
    const email = extractEmailFromVerificationToken(token);

    if (!email) {
      resendFailed();
      return;
    }

    try {
      setShowResend(false);
      await initiateVerification(email);
      toast.show("email_confirmation_resent");
    } catch {
      resendFailed();
      toast.error("email_confirmation_resend_failed");
      setShowResend(true);
    }
  };

  useEffect(() => {
    if (linkVerificationToken) {
      void verifyEmailByLink({ linkVerificationToken })
        .then(() => {
          navigate(createAppPath(Path.app, "root"), { replace: true });
          toast.show("email_confirmation_success");
        })
        .catch(() => {
          toast.error("email_confirmation_attempt_failed");
          setShowResend(true);
        });
    }
  }, []);

  if (userVerify.stateName === UserEmailVerificationStateName.unverified) {
    return <Loader />;
  }

  if (userVerify.stateName === UserEmailVerificationStateName.resendFailed) {
    return <ErrorPage />;
  }

  return (
    <ErrorPageLayout>
      <div>
        <CenterEl>
          <nine-heading>
            <nine-spacer s="xs"></nine-spacer>
            <h4 className="m0 color-c-80">
              <Translate msg="email_confirmation_failed_title" />
            </h4>
            <nine-spacer s="md"></nine-spacer>
            {showResend ? (
              <>
                <p className="m0 color-c-80">
                  <Translate msg="email_confirmation_failed_message" />
                </p>
              </>
            ) : (
              <>
                <p className="m0 color-c-80">
                  <Translate msg="email_confirmation_resent_message" />
                </p>
              </>
            )}
          </nine-heading>
          {showResend && (
            <>
              <nine-spacer s="lg"></nine-spacer>
              <Button
                onPress={(): void => {
                  void handleResend();
                }}
              >
                <Translate msg="resend_email" />
              </Button>
            </>
          )}
        </CenterEl>
      </div>
    </ErrorPageLayout>
  );
};

export default ConfirmEmailAddress;
